// Blog metadata + full-length content for 12 posts.
// Content is written as JSX component functions to allow rich typography, tables and internal links.
import { Link } from "react-router-dom";

const InternalLink = ({ to, children }) => (
  <Link to={to} className="text-sunset underline decoration-sunset/40 hover:decoration-sunset">
    {children}
  </Link>
);

// -----------------------------------------------------------
// BLOG POST CONTENT COMPONENTS
// -----------------------------------------------------------

const P1_Andaman_BestTime = () => (
  <>
    <p>
      Andaman is a year-round destination on paper, but the difference between a January
      snorkel session with 25-metre visibility and a July ferry that never leaves the harbour
      is enormous. This guide breaks the year down by month so you can pick your window with
      real information — not just "peak" and "off-peak".
    </p>

    <h2>Quick answer</h2>
    <p>
      <strong>The best time to visit Andaman is October to May</strong>, with December to March
      offering the calmest seas, clearest underwater visibility and the most reliable ferry
      schedules. If you want to dive or snorkel, this is your window.
    </p>

    <h2>Month-by-month breakdown</h2>
    <table>
      <thead><tr><th>Month</th><th>Weather</th><th>Sea</th><th>Best for</th></tr></thead>
      <tbody>
        <tr><td>January</td><td>22–29°C, dry</td><td>Calm</td><td>Diving, honeymoons, families</td></tr>
        <tr><td>February</td><td>23–30°C, dry</td><td>Calm</td><td>Diving certifications, week-long trips</td></tr>
        <tr><td>March</td><td>25–31°C, warm</td><td>Calm</td><td>Best underwater visibility of the year</td></tr>
        <tr><td>April</td><td>27–33°C, humid</td><td>Warm & calm</td><td>Budget-conscious travelers</td></tr>
        <tr><td>May</td><td>28–33°C, pre-monsoon</td><td>Occasional swell</td><td>Last window before rains</td></tr>
        <tr><td>June–August</td><td>25–30°C, heavy monsoon</td><td>Rough</td><td>Photographers, lush landscapes</td></tr>
        <tr><td>September</td><td>25–30°C, tapering rain</td><td>Improving</td><td>Deep discounts, flexible travelers</td></tr>
        <tr><td>October</td><td>24–30°C, drying up</td><td>Calming</td><td>Value + fewer crowds</td></tr>
        <tr><td>November</td><td>23–29°C, dry</td><td>Calm</td><td>Diving returns, high season begins</td></tr>
        <tr><td>December</td><td>22–28°C, dry & cool</td><td>Calm</td><td>Peak season — book early</td></tr>
      </tbody>
    </table>

    <h2>The dive-season within the dive-season</h2>
    <p>
      Underwater visibility in Andaman is genuinely world-class between February and April.
      During these months, the surface currents settle, plankton reduces and you can regularly
      see 25 to 30 metres. If your primary goal is scuba — whether an intro dive or a full
      Open Water certification — target these months.
    </p>

    <h3>Best dive months per site</h3>
    <ul>
      <li><strong>Havelock (Swaraj Dweep):</strong> December to April</li>
      <li><strong>Neil (Shaheed Dweep):</strong> January to April</li>
      <li><strong>Cinque Island trips:</strong> March to May, sea permitting</li>
    </ul>

    <h2>Monsoon in Andaman — is it worth going?</h2>
    <p>
      Yes, with caveats. Between June and September, the archipelago turns green in a way you
      won't see any other time. Rates drop by 30–50%. The catch is ferries: government and
      private catamarans (Makruzz, ITT, Green Ocean) may cancel or delay in bad weather. If
      you plan a monsoon Andaman trip, build in two buffer days and consider limiting yourself
      to Port Blair and Havelock.
    </p>

    <h3>What monsoon gets you</h3>
    <ul>
      <li>Emerald green forests and dramatic clouds</li>
      <li>Half-empty beaches even at Radhanagar</li>
      <li>30–50% cheaper stays</li>
      <li>Occasional cancelled ferries — plan buffer days</li>
    </ul>

    <h2>What about cyclones?</h2>
    <p>
      Andaman sits in the Bay of Bengal cyclone belt but rarely takes a direct hit. Most
      disturbances form north of the islands and travel west. October to November and May are
      the transitional months to watch. Any responsible Andaman tour operator will monitor IMD
      alerts and reschedule if a system forms nearby.
    </p>

    <h2>How to think about this if you're planning a trip</h2>
    <p>
      A rule of thumb we use when we build <InternalLink to="/andaman-tour-packages">Andaman tour packages</InternalLink>:
    </p>
    <ul>
      <li>If you want <em>guaranteed weather and diving</em>: December, January, February.</li>
      <li>If you want <em>best value + still-great weather</em>: October, November, April.</li>
      <li>If you want <em>photography and low crowds</em>: September, or late-October.</li>
      <li>If you want to <em>avoid crowds and cost is no object</em>: aim for the second half of January.</li>
    </ul>

    <h2>Bottom line</h2>
    <p>
      Book Andaman between mid-October and mid-May. December to March is prime for diving and
      honeymoons; late-April is a smart shoulder window. Avoid a hard-scheduled trip during
      June–August unless you're comfortable with monsoon disruption.
    </p>
  </>
);

const P2_Scuba_Andaman = () => (
  <>
    <p>
      Andaman has quietly become one of Asia's best places to try scuba diving for the first time.
      Warm water, gentle reefs, mature dive schools and a beginner-friendly island (Havelock)
      make it an easy 'yes' — provided you know what to expect. This guide walks you through
      cost, safety, the two most popular entry-level dive types, and the best sites.
    </p>

    <h2>Should you try scuba in Andaman?</h2>
    <p>
      If you can swim comfortably in a pool and you don't have a serious lung or heart
      condition, yes. You do not need to be an athlete. Modern intro dives at Havelock take
      complete beginners into 8–12 metres with a certified instructor on a one-on-one or
      one-on-two ratio.
    </p>

    <h2>Intro dive vs open water certification</h2>
    <table>
      <thead><tr><th></th><th>Discover Scuba (Intro)</th><th>Open Water Diver</th></tr></thead>
      <tbody>
        <tr><td>Duration</td><td>2–3 hours</td><td>3–4 days</td></tr>
        <tr><td>Cost</td><td>₹4,000–6,500</td><td>₹22,000–28,000</td></tr>
        <tr><td>Depth</td><td>To 12 m</td><td>To 18 m</td></tr>
        <tr><td>Certification</td><td>None — one-time experience</td><td>Lifetime PADI/SSI card, dive anywhere in the world</td></tr>
        <tr><td>Best for</td><td>First-timers, honeymooners, quick add-on</td><td>Committed beginners, dive trips</td></tr>
      </tbody>
    </table>

    <h2>Safety — what a good dive operator actually does</h2>
    <ul>
      <li>Medical questionnaire before the dive (no bluffing here — be honest)</li>
      <li>Ratio of one instructor to two guests maximum for intro dives</li>
      <li>Full equipment check on land and again in shallow water</li>
      <li>Basic skills session in 2–3 m before dropping to depth: regulator retrieval, mask clearing, equalisation</li>
      <li>Buddy check before every dive (open-water students)</li>
      <li>Dive computer or bottom-timer on every diver</li>
    </ul>
    <p>
      Look for a dive centre affiliated with PADI, SSI or SDI, and ask about their instructor
      ratio. Any centre that promises to "just take you down" without a briefing is a hard no.
    </p>

    <h2>Best dive sites in Andaman</h2>
    <h3>For absolute beginners</h3>
    <ul>
      <li><strong>Nemo Reef, Havelock:</strong> 6–8 m, tropical fish, clownfish colonies</li>
      <li><strong>House Reef, Havelock:</strong> Shore-entry, 5–10 m</li>
      <li><strong>Turtle Bay, Neil:</strong> Green turtles, shallow coral gardens</li>
    </ul>
    <h3>For open-water certified divers</h3>
    <ul>
      <li><strong>Barracuda City, Havelock:</strong> 15–25 m, schooling barracuda</li>
      <li><strong>Johnny's Gorge:</strong> Boulders, groupers, reef sharks — advanced</li>
      <li><strong>Dixon's Pinnacle:</strong> Nutrient-rich, big-fish possibilities</li>
    </ul>

    <h2>Costs — what you'll actually spend</h2>
    <p>
      All prices are per person, indicative for 2025–26. Add ~5% for peak season (Dec–Jan).
    </p>
    <ul>
      <li>Discover Scuba (single intro dive): ₹4,000–6,500</li>
      <li>Two-dive fun day for certified divers: ₹6,500–8,500</li>
      <li>PADI Open Water certification (4 days): ₹22,000–28,000</li>
      <li>Advanced Open Water (2 days, add-on): ₹18,000–22,000</li>
      <li>Underwater photos/videos: ₹1,500–3,000 add-on</li>
    </ul>

    <h2>What to bring</h2>
    <ul>
      <li>Swimsuit + rashguard (rental gear is provided)</li>
      <li>Reef-safe sunscreen (chemical sunscreens damage corals — ban is coming in)</li>
      <li>A light-coloured cotton t-shirt for surface intervals</li>
      <li>Motion-sickness tablets if you know the boat ride will affect you</li>
    </ul>

    <h2>When to dive</h2>
    <p>
      December to April, with February and March offering the best visibility (25–30 m).
      Avoid full-on monsoon dives (July–August). See our full
      {" "}<InternalLink to="/blog/best-time-to-visit-andaman">Andaman month-by-month guide</InternalLink>{" "}
      for context.
    </p>

    <h2>A word on the "cheap" dive centres</h2>
    <p>
      You'll see WhatsApp forwards offering ₹2,500 dives. Nine times out of ten these operate
      out of unaffiliated boats with unlicensed staff, one-size-fits-all masks and no medical
      screen. It's not worth the ₹2,000 you save. Book through your travel operator or a
      certified dive shop — we work only with PADI 5-star centres on
      {" "}<InternalLink to="/andaman-tour-packages">our Andaman tour packages</InternalLink>.
    </p>

    <h2>Bottom line</h2>
    <p>
      Andaman is one of the safest, most beginner-friendly places to try scuba diving in Asia.
      Choose a certified centre, be honest on the medical form, and target February–March if
      you want the water to be at its clearest.
    </p>
  </>
);

const P3_Andaman_Itinerary = () => (
  <>
    <p>
      Six days is the sweet spot for Andaman — enough to cover Port Blair, Havelock and Neil
      without feeling like you're just moving between ferries. This itinerary is the one we
      recommend to first-timers who want a balanced trip: some history in Port Blair, real
      beach time in Havelock, and one slow island day in Neil.
    </p>

    <h2>The six-day plan at a glance</h2>
    <table>
      <thead><tr><th>Day</th><th>Base</th><th>What you'll do</th></tr></thead>
      <tbody>
        <tr><td>1</td><td>Port Blair</td><td>Arrive, Cellular Jail, Light &amp; Sound show</td></tr>
        <tr><td>2</td><td>Port Blair → Havelock</td><td>Morning ferry, Radhanagar Beach sunset</td></tr>
        <tr><td>3</td><td>Havelock</td><td>Elephant Beach snorkeling / intro scuba</td></tr>
        <tr><td>4</td><td>Havelock → Neil</td><td>Ferry to Neil, Natural Bridge, Bharatpur</td></tr>
        <tr><td>5</td><td>Neil → Port Blair</td><td>Sunrise at Laxmanpur, evening return ferry</td></tr>
        <tr><td>6</td><td>Port Blair</td><td>Ross Island / North Bay, fly out</td></tr>
      </tbody>
    </table>

    <h2>Day 1 — Port Blair (arrival)</h2>
    <p>
      Land at Veer Savarkar International Airport. Check into your hotel. Most travelers
      underestimate Port Blair — it deserves at least an afternoon. Head to the Cellular Jail
      by 3:30 pm, walk the wings and the museum, and stay for the Light &amp; Sound show at
      6:00 pm. It's genuinely well-produced and it recalibrates how you'll see the rest of the
      trip. Dinner along Aberdeen Bazaar; the seafood at Annapurna is a safe choice.
    </p>

    <h2>Day 2 — Havelock (Radhanagar sunset)</h2>
    <p>
      Take the 6:30 or 8:30 am Makruzz/Green Ocean ferry — around 1.5 to 2 hours. Check in at
      Havelock. Afternoon: head to Radhanagar Beach (Beach No. 7). Get there by 4:30 pm to
      grab a spot for sunset. The sand is the whitest you'll see in India. Dinner at a shack —
      Full Moon Café or Something Different are our regulars.
    </p>

    <h2>Day 3 — Havelock (snorkeling / scuba)</h2>
    <p>
      This is the day you pick your poison. Two options:
    </p>
    <ul>
      <li><strong>Snorkeling day:</strong> Half-day boat to Elephant Beach — snorkel + glass-bottom boat, back by lunch. Afternoon at Kalapathar Beach.</li>
      <li><strong>Scuba day:</strong> Book a Discover Scuba (intro) or a two-dive fun day if you're already certified. See our <InternalLink to="/blog/scuba-diving-in-andaman-guide">scuba guide</InternalLink> for the details.</li>
    </ul>

    <h2>Day 4 — Havelock → Neil</h2>
    <p>
      Morning ferry (1 hour) to Neil Island. Neil is smaller, sleepier and greener than
      Havelock — a scooter is the best way to explore. Afternoon at Natural Bridge (Howrah
      Bridge) at low tide, then Bharatpur Beach for a light snorkel. Sunset at Laxmanpur Beach.
    </p>

    <h2>Day 5 — Neil → back to Port Blair</h2>
    <p>
      Optional 5:30 am scooter ride to Sitapur Beach for the sunrise. Late breakfast. Ferry
      back to Port Blair in the afternoon. Evening walk at Marina Park.
    </p>

    <h2>Day 6 — Ross Island or North Bay, then fly out</h2>
    <p>
      A half-day trip to Ross Island (former British admin capital, now overrun by peacocks
      and history) or North Bay for the last snorkeling of the trip. Head to the airport by
      1:00 pm for afternoon flights back to the mainland.
    </p>

    <h2>Where to stay (mid-range picks)</h2>
    <ul>
      <li><strong>Port Blair:</strong> Sea Shell Port Blair or SeaShell Casino</li>
      <li><strong>Havelock:</strong> Symphony Palms, Silver Sand, or TSG Blue</li>
      <li><strong>Neil:</strong> Sea Shell Neil, Silver Sand Neil</li>
    </ul>

    <h2>Ferry tips</h2>
    <ul>
      <li>Book Makruzz, ITT Majestic or Green Ocean at least 2 weeks in advance for December–February</li>
      <li>Reach the jetty 45 min before departure — security check is thorough</li>
      <li>Motion sickness tablets an hour before if you're prone to it</li>
    </ul>

    <h2>Budgeting (per person, mid-range)</h2>
    <ul>
      <li>Stays (5 nights, 3-star): ₹18,000</li>
      <li>Ferries: ₹4,500</li>
      <li>Airport transfers + sightseeing: ₹3,500</li>
      <li>Meals: ₹5,000</li>
      <li>Activities (intro scuba + snorkel + Ross Island): ₹7,000</li>
      <li><strong>Total (excluding flights): ₹38,000</strong></li>
    </ul>

    <h2>How we run this itinerary</h2>
    <p>
      Our <InternalLink to="/andaman-tour-packages">Andaman Explorer (5N/6D)</InternalLink> is the
      exact plan above with pre-booked ferries, hand-picked stays and a coordinator on WhatsApp
      for the entire trip.
    </p>
  </>
);

const P4_NorthSouth_Goa = () => (
  <>
    <p>
      "North Goa or South Goa?" is the first question every first-time Goa traveler asks. The
      honest answer is: they're two completely different holidays. This guide breaks down what
      each side actually feels like so you can pick — or better yet, split your trip.
    </p>

    <h2>The one-line answer</h2>
    <p>
      <strong>North Goa is loud, social, watersports-heavy and cheaper.</strong>{" "}
      <strong>South Goa is quiet, leafy, resort-driven and more expensive.</strong> Most first-timers do 60/40 North to South.
    </p>

    <h2>Side-by-side</h2>
    <table>
      <thead><tr><th></th><th>North Goa</th><th>South Goa</th></tr></thead>
      <tbody>
        <tr><td>Vibe</td><td>Party, backpacker, family beach</td><td>Slow, palm-lined, luxurious</td></tr>
        <tr><td>Best beaches</td><td>Baga, Calangute, Vagator, Anjuna, Ashwem</td><td>Palolem, Agonda, Colva, Cavelossim, Patnem</td></tr>
        <tr><td>Nightlife</td><td>Tito's Lane, Curlies, Chronicle, casinos</td><td>Almost none — dinner-then-sleep</td></tr>
        <tr><td>Watersports</td><td>Everywhere — jet ski, parasailing, banana ride at Baga & Calangute</td><td>Palolem kayaking, some parasailing</td></tr>
        <tr><td>Stays (avg mid-range)</td><td>₹3,000–5,000 / night</td><td>₹6,000–14,000 / night</td></tr>
        <tr><td>Food</td><td>Shacks + international + street</td><td>Boutique restaurants + resort dining</td></tr>
        <tr><td>Best for</td><td>Groups, first-timers, budget</td><td>Couples, families, repeat travelers</td></tr>
      </tbody>
    </table>

    <h2>North Goa in detail</h2>
    <p>
      North Goa is the version you see in movies — Vagator's cliff-top sunset, Anjuna's
      Wednesday flea market, Curlies at Baga, the buzz of shacks on every 500 metres of sand.
      It's also where every commercial watersport happens. If it's your first Goa trip and
      you're under 35, or traveling with a group, start here.
    </p>
    <h3>Base yourself in</h3>
    <ul>
      <li><strong>Baga/Calangute:</strong> Central, loud, everything close</li>
      <li><strong>Vagator/Anjuna:</strong> More boutique, cliffside, sundowners</li>
      <li><strong>Ashwem/Morjim:</strong> Quieter, upscale, popular with expats</li>
    </ul>

    <h2>South Goa in detail</h2>
    <p>
      South Goa is where Goa slows down. Palm groves, empty stretches of golden sand, an
      unhurried pace. Palolem in the far south has a distinct backpacker energy; Agonda and
      Cavelossim are for pure resort holidays. Distances are longer — the drive from Baga to
      Palolem is 90 km, roughly 2.5 hours.
    </p>
    <h3>Base yourself in</h3>
    <ul>
      <li><strong>Cavelossim/Varca:</strong> Big resorts, wide empty beaches</li>
      <li><strong>Agonda:</strong> Boutique beach huts, turtle nesting season</li>
      <li><strong>Palolem:</strong> Backpacker energy, kayaking, silent discos</li>
    </ul>

    <h2>Should you split your trip?</h2>
    <p>
      Almost always yes, if you have four or more nights. A typical 4N/5D split we run in{" "}
      <InternalLink to="/goa-tour-packages">our Goa packages</InternalLink> looks like:
    </p>
    <ul>
      <li><strong>Nights 1–2:</strong> North Goa (Baga/Vagator) — nightlife, watersports</li>
      <li><strong>Nights 3–4:</strong> South Goa (Cavelossim/Palolem) — decompress</li>
      <li><strong>Half-day 3:</strong> Old Goa churches on the drive south</li>
    </ul>

    <h2>Season considerations</h2>
    <p>
      In peak season (December), North Goa stays are 2–3x their off-season prices. In monsoon,
      most shacks are shut but the palm groves and Dudhsagar Falls are extraordinary.
      October–November is our favourite window — everything's open, prices haven't peaked yet.
    </p>

    <h2>Special cases</h2>
    <ul>
      <li><strong>Traveling with parents:</strong> Skip North Goa's chaos. Base in Cavelossim; day-trip up for Old Goa &amp; sunset cruise.</li>
      <li><strong>Bachelor/bachelorette:</strong> North, all the way. Anjuna villas + Baga shacks.</li>
      <li><strong>Honeymoon:</strong> Split — 2 nights in a boutique North Goa villa, 3 nights in a beachside cottage in Agonda.</li>
      <li><strong>Solo:</strong> Palolem in November — cheap huts, easy people to meet, no pressure.</li>
    </ul>

    <h2>Bottom line</h2>
    <p>
      Don't choose. Split your Goa trip 60/40 or 50/50 between the two sides. North gives you
      the story; South gives you the sleep. If you must pick one — go North the first time,
      South every time after.
    </p>
  </>
);

const P5_OffbeatGoa = () => (
  <>
    <p>
      Ten trips in and Goa can start to feel like the same photo you already have. The state
      is much bigger and much stranger than the shack strip suggests. This is a curated list
      of ten offbeat things to do — most of them at least an hour from the standard tourist
      circuit, and all worth the drive.
    </p>

    <h2>1. Sunrise at Cabo de Rama Fort</h2>
    <p>
      A crumbling Portuguese fort on a cliff in south Goa. The road in is unmarked and half-broken,
      which is the point. Reach by 6:00 am for sunrise over the Arabian Sea and you'll usually
      have it to yourself. Wear proper shoes — the cliff edges are unfenced.
    </p>

    <h2>2. Kayaking through the Sal River mangroves</h2>
    <p>
      Local outfits run guided kayaking trips out of Betul and Mobor. Two hours through
      mangrove backwaters, kingfishers overhead, and if you're lucky, a saltwater crocodile
      sunning on the bank. Best between October and February.
    </p>

    <h2>3. Divar Island by ferry</h2>
    <p>
      A short government ferry ride from Old Goa gets you to Divar — one of Goa's oldest
      inhabited islands, largely untouched by tourism. Rent a scooter at the pier, ride
      through 300-year-old villages, stop at Piedade church for the view.
    </p>

    <h2>4. Dudhsagar Falls in monsoon</h2>
    <p>
      Most visitors see Dudhsagar in the dry season when it's a modest cascade. In late-July
      it's a thundering 310-metre monster. Reach via jeep from Molem — you can no longer
      swim in the pool, but the view from the railway bridge is unforgettable.
    </p>

    <h2>5. Old Goa churches walking tour</h2>
    <p>
      A three-hour walk covering the Basilica of Bom Jesus (housing St. Francis Xavier's
      remains), Sé Cathedral (Asia's largest church) and the ruined St. Augustine's tower.
      Hire a heritage guide — the layered history of Portuguese Goa is worth the context.
    </p>

    <h2>6. Sunday Latin mass at St. Cajetan Church</h2>
    <p>
      Modelled on St. Peter's Basilica in Rome, this is still an active parish. Attend a
      Sunday mass (respectfully — cover shoulders) for a slice of Portuguese Goa that never
      really left.
    </p>

    <h2>7. Chorao Island bird sanctuary</h2>
    <p>
      A tiny Ramsar site inside the Mandovi estuary. Ferry from Ribandar, walk the boardwalk,
      spot the resident kingfishers, terns and (in winter) migrating ospreys. Perfect for a
      slow morning.
    </p>

    <h2>8. Spice plantation lunch at Sahakari or Savoi</h2>
    <p>
      Skip the Ponda tourist plantations. Sahakari (Curti) and Savoi (Ponda taluka) both do
      real working plantations with Konkani thali lunches on banana leaf. You'll learn how
      vanilla, cinnamon and pepper are actually grown and end up with a very full belly.
    </p>

    <h2>9. Netravali bubble lake</h2>
    <p>
      A small forest lake near Netravali where methane bubbles rise continuously. Local legend
      attributes it to a river-god. Combine with a short trek to Netravali waterfall — one of
      the least-visited big waterfalls in the state.
    </p>

    <h2>10. Fontainhas heritage walk</h2>
    <p>
      Panjim's Latin Quarter — narrow lanes, ochre and blue houses, red-tiled roofs, small
      wine bars. Do it at 6:30 pm as the streetlights come on. This is Goa's most walkable
      neighbourhood and a photographer's dream.
    </p>

    <h2>Building it into your trip</h2>
    <p>
      Most of these need half a day; a few (Dudhsagar, Divar) need a full one. In our{" "}
      <InternalLink to="/goa-tour-packages">Goa tour packages</InternalLink> we typically weave two
      or three of these into a standard 4–5 night trip so it doesn't feel like a beach
      slideshow.
    </p>
  </>
);

const P6_GoaWatersports = () => (
  <>
    <p>
      Watersports in Goa is a slightly wild ecosystem — hundreds of operators, wildly varying
      prices, and a real safety spread. This guide covers the actual price bands you should
      expect, the safest beaches to try each activity, and the questions to ask before you
      strap into anything.
    </p>

    <h2>What's on offer</h2>
    <ul>
      <li><strong>Parasailing:</strong> Pulled behind a speedboat under a parachute. 10 min flight, 60–100 m altitude.</li>
      <li><strong>Jet ski:</strong> Solo or double ride, 10 min, along the coast.</li>
      <li><strong>Banana ride:</strong> Inflatable banana pulled by boat. Groups of 4–6.</li>
      <li><strong>Bumper ride / Sofa ride:</strong> Similar to banana, higher speed.</li>
      <li><strong>Speed boat:</strong> 15-min ride out to sea and back.</li>
      <li><strong>Kayaking:</strong> Palolem, Agonda, backwaters.</li>
      <li><strong>Scuba diving:</strong> Grande Island (South Goa) — separate outing.</li>
      <li><strong>Windsurfing / kite surfing:</strong> Ashwem, Morjim, seasonal.</li>
    </ul>

    <h2>Prices (2025–26)</h2>
    <table>
      <thead><tr><th>Activity</th><th>Duration</th><th>Price / person</th></tr></thead>
      <tbody>
        <tr><td>Parasailing</td><td>10 min flight</td><td>₹1,200–1,800</td></tr>
        <tr><td>Jet ski (solo)</td><td>10 min</td><td>₹500–800</td></tr>
        <tr><td>Jet ski (double)</td><td>10 min</td><td>₹800–1,200</td></tr>
        <tr><td>Banana ride</td><td>10 min</td><td>₹300–500</td></tr>
        <tr><td>Bumper / Sofa</td><td>10 min</td><td>₹500–800</td></tr>
        <tr><td>Speed boat</td><td>15 min</td><td>₹500–800</td></tr>
        <tr><td>Combo (5 activities)</td><td>Half-day</td><td>₹1,800–2,800</td></tr>
        <tr><td>Grande Island scuba</td><td>Full day</td><td>₹3,500–5,500</td></tr>
        <tr><td>Kayak rental</td><td>1 hour</td><td>₹300–600</td></tr>
      </tbody>
    </table>

    <h2>Best beaches by activity</h2>
    <h3>Parasailing &amp; jet skis</h3>
    <ul>
      <li><strong>Baga Beach:</strong> The biggest hub. Highest volume, most competitive prices.</li>
      <li><strong>Calangute Beach:</strong> Slightly cheaper, similar operators.</li>
      <li><strong>Candolim:</strong> Better beach, slightly less crowded.</li>
      <li><strong>Colva/Benaulim:</strong> South Goa's watersport strip. Less crowded, similar prices.</li>
    </ul>
    <h3>Kayaking</h3>
    <ul>
      <li><strong>Palolem Beach:</strong> Calm bay, perfect for beginners.</li>
      <li><strong>Agonda:</strong> Slightly more open water; better for a workout.</li>
      <li><strong>Sal River backwaters (Mobor):</strong> Guided kayak trips through mangroves.</li>
    </ul>
    <h3>Scuba</h3>
    <p>
      Only Grande Island (off South Goa) — half-day boat trip out of Vasco or Bogmalo.
      Visibility is 5–10 m; not as good as Andaman, but a solid intro dive experience if
      you're already in Goa.
    </p>

    <h2>Safety — questions to ask before you strap in</h2>
    <ol>
      <li>Do you have a certified Coast Guard permit? (Ask to see it.)</li>
      <li>Is the boat &lt; 5 years old and serviced this season?</li>
      <li>Do life jackets fit properly? (Reject any that hang loose.)</li>
      <li>How long has the boat driver been operating?</li>
      <li>What's your protocol if someone panics mid-flight/ride?</li>
    </ol>
    <p>
      A good operator will happily answer. If they get defensive, walk away — there are 20
      other operators within 500 metres.
    </p>

    <h2>When to do watersports</h2>
    <p>
      Season runs from mid-October to end-May. Operators close during peak monsoon
      (June–September). Best conditions: December–February, morning window (7:30–11:00 am)
      when the sea is calmest.
    </p>

    <h2>Should you buy the combo pack?</h2>
    <p>
      Yes — the 5-activity combo at ₹1,800–2,800 usually undercuts booking each activity
      separately by 30–40%. Just make sure you actually want all five; the sofa ride is
      typically the one people regret.
    </p>

    <h2>Bottom line</h2>
    <p>
      Baga and Candolim in the north, Colva and Benaulim in the south — those are your
      watersport hubs. Get combo packages, pay attention to safety gear, and never haggle
      down to the point where the operator starts skipping steps. See our{" "}
      <InternalLink to="/goa-tour-packages">Goa Adventure &amp; Watersports package</InternalLink>{" "}
      for a pre-vetted operator.
    </p>
  </>
);

const P7_Rajasthan_Itinerary = () => (
  <>
    <p>
      Seven days is a genuine Rajasthan trip — enough for four of the state's four
      heavyweights (Jaipur, Jodhpur, Udaipur, Jaisalmer) without ending up in the car for
      more than four hours a day. This is the itinerary we run most often, with realistic
      driving times and clear rest windows.
    </p>

    <h2>The seven-day plan</h2>
    <table>
      <thead><tr><th>Day</th><th>Base</th><th>Route</th></tr></thead>
      <tbody>
        <tr><td>1</td><td>Jaipur</td><td>Arrival, evening at Nahargarh Fort</td></tr>
        <tr><td>2</td><td>Jaipur</td><td>Amber Fort, City Palace, Hawa Mahal</td></tr>
        <tr><td>3</td><td>Jaipur → Jodhpur</td><td>Drive 6h; check-in in the blue city</td></tr>
        <tr><td>4</td><td>Jodhpur</td><td>Mehrangarh Fort, Jaswant Thada, blue city walk</td></tr>
        <tr><td>5</td><td>Jodhpur → Jaisalmer</td><td>Drive 5h; desert camp check-in</td></tr>
        <tr><td>6</td><td>Jaisalmer</td><td>Jaisalmer Fort, camel safari, folk evening</td></tr>
        <tr><td>7</td><td>Fly out</td><td>Morning departure from Jaisalmer or Jodhpur</td></tr>
      </tbody>
    </table>
    <p>
      For a longer trip, add 2 nights in <strong>Udaipur</strong> between Jodhpur and
      Jaisalmer.
    </p>

    <h2>Day 1 — Jaipur arrival</h2>
    <p>
      Land in Jaipur mid-morning or early afternoon. Check into a heritage haveli (we like
      Samode Haveli and Rambagh Palace at the high end, Diggi Palace and 28 Kothi at
      boutique). Late afternoon drive up to Nahargarh Fort for the sunset over the pink city.
      Dinner at Padao restaurant on the ramparts.
    </p>

    <h2>Day 2 — Jaipur full day</h2>
    <p>
      Early start (7:30 am) for Amber Fort to beat the crowds and heat. Take the jeep ride up
      (elephant rides are being phased out and we don't recommend them). Ganesh Pol and the
      Sheesh Mahal are the highlights. Back to town by lunch. Afternoon: City Palace, Jantar
      Mantar, and finish at Hawa Mahal by 5:00 pm for the light.
    </p>

    <h2>Day 3 — Jaipur → Jodhpur</h2>
    <p>
      Long drive (6 hours). Stop at Pushkar en route (optional, adds an hour). Check in at
      Jodhpur by evening. Sunset thali dinner on a rooftop overlooking the fort — Indique or
      Cafe Sheesh Mahal.
    </p>

    <h2>Day 4 — Jodhpur</h2>
    <p>
      Mehrangarh Fort in the morning (the elevator is worth the ₹100 upgrade — the climb is
      brutal in summer). Do the audio guide; it's genuinely well-produced. Afternoon: blue
      city walking tour through Navchokiya, ending at Toorji Ka Jhalra stepwell. Evening:
      chai at the Stepwell Cafe.
    </p>

    <h2>Day 5 — Jodhpur → Jaisalmer</h2>
    <p>
      Five-hour drive west into the Thar. The landscape starts to change around Osian and
      becomes properly desert by Pokhran. Reach Jaisalmer by evening; check straight into
      your desert camp at Sam or Khuri. Cultural evening: Kalbelia dancers, fire, dal-baati.
    </p>

    <h2>Day 6 — Jaisalmer</h2>
    <p>
      Morning: Jaisalmer Fort — one of the few 'living forts' in the world where people still
      live inside the walls. Visit Patwon Ki Haveli and Salim Singh Ki Haveli. Late
      afternoon: camel safari on the dunes for sunset. Overnight in the desert camp again if
      you're doing two nights.
    </p>

    <h2>Day 7 — Departure</h2>
    <p>
      Morning flight from Jaisalmer or drive to Jodhpur (6h) for a wider flight choice.
    </p>

    <h2>Driving realities</h2>
    <ul>
      <li>All inter-city drives are 4–7 hours on decent state highways</li>
      <li>Use a chauffeured Innova/Xylo — self-driving Rajasthan is unnecessarily stressful</li>
      <li>Break up long drives with a proper lunch stop (Chokhi Dhani highway outlets, or local dhabas)</li>
    </ul>

    <h2>Where to stay</h2>
    <ul>
      <li><strong>Jaipur:</strong> Samode Haveli, Diggi Palace, 28 Kothi (boutique)</li>
      <li><strong>Jodhpur:</strong> RAAS, Ajit Bhawan, Pal Haveli</li>
      <li><strong>Jaisalmer:</strong> Suryagarh (luxury), Serai (desert luxury), Fort Rajwada (mid-range)</li>
    </ul>

    <h2>Budget (per person, mid-range, 7N)</h2>
    <ul>
      <li>Heritage stays: ₹35,000</li>
      <li>AC vehicle + driver: ₹18,000</li>
      <li>Monument entries + guides: ₹4,500</li>
      <li>Meals: ₹8,000</li>
      <li>Camel safari + camp: ₹6,000</li>
      <li><strong>Total (excl. flights): ~₹71,500</strong></li>
    </ul>

    <h2>How we run this</h2>
    <p>
      Our <InternalLink to="/rajasthan-tour-packages">Royal Rajasthan Circuit (6N/7D)</InternalLink>{" "}
      is this itinerary, with a 4-star heritage upgrade option and a local Rajasthani guide for
      the fort days.
    </p>
  </>
);

const P8_JaisalmerCamps = () => (
  <>
    <p>
      Everyone's Jaisalmer photos look the same until you sleep in a bad desert camp. This is a
      no-nonsense guide to what desert camping in Jaisalmer actually is, how the two main
      dune-zones (Sam and Khuri) differ, and how to pick a camp that isn't a paint-and-tinsel
      operation.
    </p>

    <h2>What a desert camp actually is</h2>
    <p>
      It's a permanent tented camp — Swiss tents on wooden bases, with attached bathrooms
      (hot water in premium camps), a shared dining tent, and a nightly cultural programme.
      You are not sleeping under the stars in a bedroll. That's a different (and mostly
      unavailable) experience.
    </p>

    <h2>Sam vs Khuri dunes</h2>
    <table>
      <thead><tr><th></th><th>Sam</th><th>Khuri</th></tr></thead>
      <tbody>
        <tr><td>Distance from Jaisalmer</td><td>42 km</td><td>48 km</td></tr>
        <tr><td>Crowd</td><td>Very high, especially Oct–Feb</td><td>Low</td></tr>
        <tr><td>Dune size</td><td>Bigger, more theatrical</td><td>Smaller, more intimate</td></tr>
        <tr><td>Camel safaris</td><td>Everywhere — sometimes chaotic</td><td>Village-run, less commercial</td></tr>
        <tr><td>Cultural show</td><td>Full-stage Kalbelia + Rajasthani folk</td><td>Small, intimate</td></tr>
        <tr><td>Best for</td><td>First-timers, groups</td><td>Couples, photographers</td></tr>
        <tr><td>Price band</td><td>₹1,800–35,000+</td><td>₹1,500–15,000</td></tr>
      </tbody>
    </table>

    <h2>What to expect on a standard desert camp evening</h2>
    <ul>
      <li>4:30 pm — arrival, welcome drink (chai / lassi)</li>
      <li>5:00 pm — camel safari or 4x4 desert drive to a sunset dune</li>
      <li>6:30 pm — sunset with light snacks and (if you want it) a beer</li>
      <li>7:30 pm — back to camp, freshen up</li>
      <li>8:00 pm — cultural performance (folk singers, Kalbelia dance, fire show)</li>
      <li>9:00 pm — dinner (dal baati churma, laal maans if it's on the menu, jalebi)</li>
      <li>10:30 pm — camp fire, then bed</li>
    </ul>

    <h2>Camp tiers — what your money buys</h2>
    <h3>Budget (₹1,500–3,500 pp)</h3>
    <p>
      Basic Swiss tent, shared bathrooms or a bathroom with erratic hot water. Fine for one
      night; not memorable but not terrible. Common at Sam.
    </p>
    <h3>Standard (₹3,500–8,000 pp)</h3>
    <p>
      Attached bathroom with hot water, proper bed, buffet dinner. Most travelers should aim
      here. Camps we've used repeatedly: Prince Desert Camp, Damodra Desert Camp, Osian Sarai.
    </p>
    <h3>Luxury (₹8,000–20,000 pp)</h3>
    <p>
      Boutique canvas suites with wood-burning stoves, private bathrooms with clawfoot tubs,
      curated menus. Suryagarh (though technically a fort-hotel, not a camp) and The Serai by
      Sujan are the two names to know.
    </p>

    <h2>How to pick a camp</h2>
    <ol>
      <li>Ask for photos of the actual tent interior (not the tent exterior)</li>
      <li>Confirm attached bathroom + hot water (bucket hot water is fine; no hot water is a no)</li>
      <li>Check if dinner is buffet or à la carte — buffets in low season can be lukewarm</li>
      <li>Ask about the cultural programme timing — some camps do it at 8:00 pm, some at 9:30 pm</li>
      <li>Verify camel safari — is it a real 30-min ride, or a 5-minute selfie experience?</li>
    </ol>

    <h2>Season</h2>
    <p>
      October to March is the only sensible window. November nights hit 8–12°C — bring a
      proper jacket, camps only give you a light shawl. April onwards, daytime becomes
      punishing and most standard camps close by end-May.
    </p>

    <h2>Common mistakes</h2>
    <ul>
      <li>Doing two nights at the same camp — one night is usually enough</li>
      <li>Booking through a random WhatsApp forward without checking Google reviews</li>
      <li>Assuming the "luxury tent" in the brochure = luxury tent</li>
      <li>Skipping a jacket in winter</li>
    </ul>

    <h2>Bottom line</h2>
    <p>
      Book Sam if you want the full theatrical experience and don't mind crowds. Book Khuri
      if you want a quieter, more authentic desert night. Spend ₹4,000–8,000 pp on the camp
      itself — anything less is usually a compromise. Our{" "}
      <InternalLink to="/rajasthan-tour-packages">Jaisalmer Desert Expedition</InternalLink>{" "}
      uses two pre-vetted camps at Sam and Khuri.
    </p>
  </>
);

const P9_ForstsPalaces = () => (
  <>
    <p>
      Rajasthan has more than 300 forts and palaces. Doing all of them is a lifetime project;
      doing the right ten in a two-week trip is very achievable. This is our ranked list of
      the ten monuments actually worth planning your itinerary around.
    </p>

    <h2>The top ten (ranked)</h2>

    <h3>1. Mehrangarh Fort, Jodhpur</h3>
    <p>
      The one everyone should see first. 400 feet above the blue city, filled with a
      genuinely well-curated museum and — critically — an audio guide that isn't a bore.
      The view of the blue city from the ramparts is Rajasthan's single best image.
    </p>

    <h3>2. Amber Fort, Jaipur</h3>
    <p>
      A hill fort with the Sheesh Mahal (mirror palace) as the highlight. Get here by 7:30 am
      to beat the crowds. Skip the elephant ride; take the jeep or walk.
    </p>

    <h3>3. City Palace, Udaipur</h3>
    <p>
      Sprawling lakeside palace with the Zenana Mahal, Crystal Gallery and a boat jetty to
      Jagmandir. Two hours minimum; three is better.
    </p>

    <h3>4. Jaisalmer Fort</h3>
    <p>
      One of the few 'living forts' in the world — 4,000 people still live inside the walls.
      Walk in via the main gate, wander the Jain temples, have lunch at a rooftop cafe with
      the sandstone glowing below.
    </p>

    <h3>5. Chittorgarh Fort</h3>
    <p>
      The largest fort in India by area (280 hectares). Requires a full day. Vijay Stambh
      (Tower of Victory), Rani Padmini's Palace, Meera Temple. The history is heavy — this
      is where Padmini and her consort committed jauhar rather than surrender.
    </p>

    <h3>6. Kumbhalgarh Fort</h3>
    <p>
      The Great Wall of India — 36 km of unbroken fort wall. UNESCO-listed. Best combined
      with a Ranakpur Jain Temple stop on the same day trip from Udaipur.
    </p>

    <h3>7. City Palace, Jaipur</h3>
    <p>
      Central Jaipur. Chandra Mahal is still occupied by the royal family. Peacock Gate in
      Pritam Niwas Chowk is Instagram's favourite spot for a reason.
    </p>

    <h3>8. Hawa Mahal, Jaipur</h3>
    <p>
      Not technically a palace to enter, but a 5-storey pink sandstone facade of latticed
      windows. Best at sunrise (facing east); go inside for the view of the old city from
      the top.
    </p>

    <h3>9. Lake Palace / Jag Mandir, Udaipur</h3>
    <p>
      Two floating palaces on Lake Pichola. Lake Palace is a Taj hotel now, and only
      accessible to guests. Jag Mandir has a public boat and a small museum. Sunset boat
      ride is essential — every hotel books tickets in advance.
    </p>

    <h3>10. Junagarh Fort, Bikaner</h3>
    <p>
      Unusually intact — it was never conquered. The murals inside are unrestored and better
      for it. Bikaner is a slower town — combine with the National Camel Research Centre.
    </p>

    <h2>Ones we didn't rank (but you might)</h2>
    <ul>
      <li>Nahargarh Fort (Jaipur) — sunset over the pink city, more restaurant than fort</li>
      <li>Sonar Quila / Jaisalmer Fort — see above</li>
      <li>Ranthambore Fort — tiger safari + fort combo</li>
      <li>Bhangarh Fort — the "haunted" one; go for the story, not the architecture</li>
    </ul>

    <h2>Season</h2>
    <p>
      October to March. April onwards, the sandstone walls hold heat like an oven. Guides at
      most monuments do 45–60 min tours starting at ~₹500. Booking a Deep Dive Trips guide
      via our{" "}
      <InternalLink to="/rajasthan-tour-packages">Rajasthan packages</InternalLink> is usually
      cheaper than the counter rate and better trained.
    </p>

    <h2>Bottom line</h2>
    <p>
      A two-week Rajasthan itinerary can comfortably cover 6–7 of the top 10. A one-week
      trip realistically covers 3–4 (usually Amber, City Palace Jaipur, Mehrangarh,
      Jaisalmer). Pick 3–4 and go deep; don't try to see all ten and end up seeing none.
    </p>
  </>
);

const P10_Ladakh_Bike = () => (
  <>
    <p>
      A Ladakh bike trip is one of India's genuine bucket-list rides. It's also one of the
      easiest to get wrong. This is a complete planning guide covering routes, permits, gear,
      costs, and — the part most guides ignore — realistic day-by-day distances.
    </p>

    <h2>The two big routes</h2>
    <h3>Manali → Leh</h3>
    <ul>
      <li><strong>Distance:</strong> 475 km</li>
      <li><strong>Days:</strong> 2–3 riding days</li>
      <li><strong>Key passes:</strong> Rohtang, Baralacha La, Nakee La, Lachulung La, Tanglang La</li>
      <li><strong>Season:</strong> Mid-May to late-October (BRO clears the pass)</li>
      <li><strong>Best for:</strong> Riders comfortable with high altitude, tough passes</li>
    </ul>
    <h3>Srinagar → Leh</h3>
    <ul>
      <li><strong>Distance:</strong> 435 km</li>
      <li><strong>Days:</strong> 2 riding days</li>
      <li><strong>Key passes:</strong> Zoji La, Namika La, Fotu La</li>
      <li><strong>Season:</strong> Early May to November</li>
      <li><strong>Best for:</strong> Gentler altitude gain, better acclimatization</li>
    </ul>
    <p>
      <strong>Our recommendation:</strong> Fly to Srinagar, ride Srinagar → Leh (2 days). Loop
      through Nubra and Pangong from Leh. Fly out of Leh. Less altitude stress, gentler start.
    </p>

    <h2>Route: full Srinagar-Leh-Manali loop</h2>
    <table>
      <thead><tr><th>Day</th><th>From → To</th><th>Distance</th><th>Highlight</th></tr></thead>
      <tbody>
        <tr><td>1</td><td>Srinagar</td><td>—</td><td>Acclimatize, permits</td></tr>
        <tr><td>2</td><td>Srinagar → Kargil</td><td>200 km</td><td>Zoji La</td></tr>
        <tr><td>3</td><td>Kargil → Leh</td><td>230 km</td><td>Lamayuru monastery</td></tr>
        <tr><td>4</td><td>Leh</td><td>—</td><td>Acclimatize + local sights</td></tr>
        <tr><td>5</td><td>Leh → Nubra (via Khardung La)</td><td>125 km</td><td>Khardung La 5,359 m</td></tr>
        <tr><td>6</td><td>Nubra → Pangong (via Shyok)</td><td>170 km</td><td>Shyok river valley</td></tr>
        <tr><td>7</td><td>Pangong → Leh</td><td>160 km</td><td>Chang La</td></tr>
        <tr><td>8</td><td>Leh → Sarchu</td><td>250 km</td><td>Tanglang La</td></tr>
        <tr><td>9</td><td>Sarchu → Manali</td><td>225 km</td><td>Baralacha La, Rohtang</td></tr>
      </tbody>
    </table>

    <h2>Bike choice</h2>
    <p>
      Royal Enfield Himalayan is purpose-built for these roads and our default choice. Classic
      350 works but feels underpowered on the passes. KTM 390 Adventure and the new
      Himalayan 450 are excellent alternatives if you're an experienced rider.
    </p>

    <h2>Permits</h2>
    <ul>
      <li><strong>Inner Line Permit (ILP)</strong> for Indian nationals — for Nubra, Pangong, Tso Moriri, Hanle, Turtuk. Available online via Leh District website or at the DC office. ₹400 base + ₹20/day per person.</li>
      <li><strong>Protected Area Permit (PAP)</strong> for foreign nationals — same locations. Only via registered agents.</li>
    </ul>
    <p>
      We arrange all permits as part of our{" "}
      <InternalLink to="/leh-ladakh-tour-packages">Ladakh bike expedition</InternalLink>.
    </p>

    <h2>Gear checklist</h2>
    <ul>
      <li>DOT/ECE-certified full-face helmet</li>
      <li>Riding jacket with CE-certified armour</li>
      <li>Riding gloves (winter-rated)</li>
      <li>Knee guards, elbow guards</li>
      <li>Waterproof over-pants</li>
      <li>Layered thermals — nights hit -5°C in September</li>
      <li>Buff / balaclava — dust protection at Khardung La is real</li>
      <li>Sunglasses (UV400) — UV at altitude burns skin in 20 min</li>
      <li>Sunscreen SPF 50+, lip balm with SPF</li>
      <li>Diamox for AMS (consult your doctor)</li>
    </ul>

    <h2>Costs (per person, 9-day loop)</h2>
    <ul>
      <li>Bike rental (Himalayan) with fuel: ₹22,000</li>
      <li>Support vehicle (shared, 5 riders): ₹8,000</li>
      <li>Stays (mix of camps + hotels): ₹18,000</li>
      <li>Food (breakfast + dinner included): ₹6,000</li>
      <li>Permits + entry fees: ₹2,000</li>
      <li>Buffer / repairs: ₹3,000</li>
      <li><strong>Total: ~₹59,000</strong> (excluding flights to Srinagar / from Leh)</li>
    </ul>

    <h2>Common mistakes</h2>
    <ol>
      <li>Riding into Nubra or Pangong on Day 2 in Leh — a fast track to altitude sickness</li>
      <li>Underestimating cold — even July nights at Pangong are 3–8°C</li>
      <li>Skipping the support vehicle — one puncture becomes a 6-hour nightmare</li>
      <li>Fuel planning — always tank up in Leh, next reliable pump is Karu (43 km)</li>
      <li>Riding after dark — animals, potholes, hypothermia risk</li>
    </ol>

    <h2>Bottom line</h2>
    <p>
      Do Srinagar → Leh in, out via Manali. Nine days. Rent a Himalayan. Ride with a support
      vehicle. Take altitude sickness seriously — read our{" "}
      <InternalLink to="/blog/how-to-avoid-altitude-sickness-in-ladakh">AMS guide</InternalLink>{" "}
      before you go.
    </p>
  </>
);

const P11_Ladakh_AMS = () => (
  <>
    <p>
      Altitude sickness ruins more Ladakh trips than anything else — including weather,
      breakdowns, or bad hotels. This guide covers exactly what AMS is, how to recognise it,
      and how our itineraries plan around it. Read this before you go.
    </p>

    <h2>What is acute mountain sickness (AMS)?</h2>
    <p>
      AMS is your body's reaction to reduced oxygen at altitude. It kicks in above 2,500 m and
      is common above 3,500 m — which is where Leh sits. Symptoms include headache, nausea,
      loss of appetite, dizziness, difficulty sleeping and shortness of breath. Severe AMS
      progresses to HAPE (High-Altitude Pulmonary Edema) or HACE (High-Altitude Cerebral
      Edema), both of which are medical emergencies.
    </p>

    <h2>The golden rule of Ladakh</h2>
    <p>
      <strong>Do nothing for the first 24 hours after landing in Leh.</strong> No sightseeing,
      no shopping, no early lunch out. Rest, hydrate, sleep. This one habit prevents 80% of
      AMS problems.
    </p>

    <h2>The Deep Dive Trips acclimatization plan</h2>
    <table>
      <thead><tr><th>Day</th><th>Altitude</th><th>Plan</th></tr></thead>
      <tbody>
        <tr><td>1</td><td>Leh (3,500 m)</td><td>Rest. Only room + hotel restaurant.</td></tr>
        <tr><td>2</td><td>Leh</td><td>Gentle Leh Palace + Shanti Stupa walk. Early night.</td></tr>
        <tr><td>3</td><td>Leh + Sham Valley (3,300 m)</td><td>Low-altitude day trip — Alchi, Likir, Magnetic Hill</td></tr>
        <tr><td>4</td><td>Leh → Nubra (Khardung La 5,359 m; sleep at 3,050 m)</td><td>Cross the pass, sleep low</td></tr>
        <tr><td>5</td><td>Nubra (3,050 m)</td><td>Slow day — Diskit monastery, dunes</td></tr>
        <tr><td>6</td><td>Pangong (4,250 m)</td><td>Drive via Shyok, sleep at Pangong</td></tr>
        <tr><td>7</td><td>Leh</td><td>Return</td></tr>
      </tbody>
    </table>
    <p>
      Notice: three nights in Leh before going higher. Notice: we never sleep at Khardung La
      or Chang La — we cross and drop. Notice: Pangong is one night, not two.
    </p>

    <h2>Diamox — do I need it?</h2>
    <p>
      Diamox (acetazolamide) accelerates acclimatization. It works well but has side effects
      (numbness in fingertips, more urination, weird tingling). We recommend it for:
    </p>
    <ul>
      <li>Anyone who has had AMS before</li>
      <li>Anyone flying directly to Leh (as opposed to road)</li>
      <li>Anyone above 45, especially with hypertension</li>
    </ul>
    <p>
      Dose: 125–250 mg twice daily, starting one day before arrival, for three days. Always
      consult a doctor first — it interacts with sulfa allergies and some heart medications.
    </p>

    <h2>Warning signs — when to descend immediately</h2>
    <ul>
      <li>Headache that doesn't respond to paracetamol</li>
      <li>Vomiting more than once</li>
      <li>Persistent dry cough or wet cough with pink-tinged sputum (possible HAPE)</li>
      <li>Confusion, slurred speech, difficulty walking straight (possible HACE)</li>
      <li>Bluish lips or fingertips (cyanosis)</li>
    </ul>
    <p>
      If you see any of these, descend to lower altitude immediately. Do not wait to "see if
      it improves". Leh Sonam Norboo Memorial Hospital handles AMS emergencies 24×7.
    </p>

    <h2>What to do (and not do)</h2>
    <p><strong>Do:</strong></p>
    <ul>
      <li>Drink 3–4 litres of water a day</li>
      <li>Eat light, high-carb meals</li>
      <li>Sleep low — never at the top of a pass</li>
      <li>Take rest days seriously</li>
    </ul>
    <p><strong>Do not:</strong></p>
    <ul>
      <li>Drink alcohol for the first 48 hours</li>
      <li>Smoke</li>
      <li>Run, hike or exert yourself on day 1–2</li>
      <li>Sleep at Khardung La ("we'll just spend the night on top") — no.</li>
    </ul>

    <h2>Flying in vs driving in</h2>
    <p>
      Flying to Leh is far more likely to cause AMS because you gain 3,500 m in an hour.
      Driving from Manali or Srinagar naturally builds up over 2–3 days. If you're prone to
      AMS or older, drive in and fly out.
    </p>

    <h2>Kids and elderly travellers</h2>
    <p>
      We generally advise against Ladakh for children under 5 and adults over 70, especially
      if they have any respiratory or cardiac condition. Get a doctor's clearance before
      booking. Bring pulse oximeters — a reading below 85% at rest is a red flag.
    </p>

    <h2>Bottom line</h2>
    <p>
      Ladakh is worth the extra caution. Give yourself two full days in Leh before going
      higher. Sleep low, drink water, eat light. Read symptoms honestly and descend if you
      have to. Our{" "}
      <InternalLink to="/leh-ladakh-tour-packages">Ladakh tour packages</InternalLink> are all
      built around this acclimatization principle.
    </p>
  </>
);

const P12_Pangong_TsoMoriri_Nubra = () => (
  <>
    <p>
      If you have five to seven days in Ladakh, you can't do everything. Most travelers end
      up choosing between Pangong, Tso Moriri and Nubra — and end up regretting the choice
      later. This guide breaks down how to think about the three, and how to build the route
      that's right for your trip.
    </p>

    <h2>The three, in one line</h2>
    <ul>
      <li><strong>Pangong Tso:</strong> The iconic blue lake. Instagram's Ladakh. Crowded and worth it.</li>
      <li><strong>Nubra Valley:</strong> The desert-and-monastery valley beyond Khardung La. Great to sleep in.</li>
      <li><strong>Tso Moriri:</strong> The quiet, remote, high-altitude second lake. For repeat visitors.</li>
    </ul>

    <h2>Side-by-side</h2>
    <table>
      <thead><tr><th></th><th>Pangong</th><th>Nubra</th><th>Tso Moriri</th></tr></thead>
      <tbody>
        <tr><td>Altitude</td><td>4,250 m</td><td>3,050 m</td><td>4,522 m</td></tr>
        <tr><td>Drive from Leh</td><td>5h via Chang La</td><td>4.5h via Khardung La</td><td>6.5h</td></tr>
        <tr><td>Crowd</td><td>High</td><td>Moderate</td><td>Very low</td></tr>
        <tr><td>Best for</td><td>The lake itself</td><td>Sleep + dunes + monasteries</td><td>Photography, solitude</td></tr>
        <tr><td>Overnight stays</td><td>Tents / homestays</td><td>Camps + hotels</td><td>Basic tents / homestays</td></tr>
        <tr><td>Cell reception</td><td>Postpaid only, patchy</td><td>Postpaid only</td><td>Almost none</td></tr>
      </tbody>
    </table>

    <h2>Building your ideal route</h2>
    <h3>5-day trip (first-timer, wants the highlights)</h3>
    <ul>
      <li>Day 1: Leh (acclimatize)</li>
      <li>Day 2: Leh (Sham Valley day-trip)</li>
      <li>Day 3: Leh → Nubra (Khardung La)</li>
      <li>Day 4: Nubra → Pangong (via Shyok)</li>
      <li>Day 5: Pangong → Leh, fly out</li>
    </ul>
    <p>This is our <InternalLink to="/leh-ladakh-tour-packages">Leh-Pangong-Nubra Classic</InternalLink> — the most requested Ladakh trip.</p>

    <h3>7-day trip (adds Tso Moriri)</h3>
    <ul>
      <li>Day 1–2: Leh (acclimatize)</li>
      <li>Day 3: Leh → Nubra</li>
      <li>Day 4: Nubra → Pangong via Shyok</li>
      <li>Day 5: Pangong → Tso Moriri (via Chushul or Nyoma — long drive)</li>
      <li>Day 6: Tso Moriri → Leh</li>
      <li>Day 7: Leh, fly out</li>
    </ul>

    <h3>7-day trip (adds Hanle instead of Tso Moriri — for stargazing)</h3>
    <ul>
      <li>Day 1–2: Leh</li>
      <li>Day 3: Leh → Nubra</li>
      <li>Day 4: Nubra → Pangong</li>
      <li>Day 5: Pangong → Hanle</li>
      <li>Day 6: Hanle → Leh</li>
      <li>Day 7: Fly out</li>
    </ul>

    <h2>Common questions</h2>
    <h3>"Is Pangong worth the crowd?"</h3>
    <p>
      Yes, in September and October, when crowds thin out. In peak July, you'll share the
      shore with 500+ people. Stay overnight (not day-trip) — sunrise on Pangong is genuinely
      spectacular and most day-trippers miss it.
    </p>
    <h3>"Can I do Pangong as a day trip from Leh?"</h3>
    <p>
      Technically yes (10–12 hours round trip) — but you'll spend the entire day in the car.
      Not recommended.
    </p>
    <h3>"Which is the best lake overall?"</h3>
    <p>
      Pangong for iconography, Tso Moriri for atmosphere. If you have to pick one, Pangong
      the first time, Tso Moriri the second.
    </p>

    <h2>The Shyok vs Chang La question</h2>
    <p>
      There are two ways to get from Nubra to Pangong: over Wari La and Chang La, or along
      the Shyok river valley. The Shyok route is shorter, lower altitude (better for
      acclimatization), and much prettier. Only downside: it can be closed during heavy
      rain. Our default: Shyok in July–September; Chang La in October when Shyok gets snow.
    </p>

    <h2>Bottom line</h2>
    <p>
      5 days: Leh + Nubra + Pangong. 7 days: add Tso Moriri or Hanle. First-timers should
      not try to do all three in a 5-day trip — you'll spend more time in the SUV than
      outside it. Read our{" "}
      <InternalLink to="/blog/how-to-avoid-altitude-sickness-in-ladakh">AMS guide</InternalLink>{" "}
      before deciding your final route.
    </p>
  </>
);

// -----------------------------------------------------------
// EXPORT ARRAY
// -----------------------------------------------------------
export const BLOGS = [
  {
    slug: "best-time-to-visit-andaman",
    title: "Best Time to Visit Andaman: Month-by-Month Guide",
    excerpt: "October to May is the sweet spot for Andaman, but which month is truly best depends on whether you want to dive, save money, or avoid crowds.",
    category: "Andaman",
    date: "2025-11-08",
    author: "Deep Dive Team",
    readingTime: "6 min read",
    tags: ["andaman", "planning", "weather"],
    image: "https://images.pexels.com/photos/37949152/pexels-photo-37949152.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Turquoise Andaman waters and white sand beach at Jolly Buoy Island",
    Content: P1_Andaman_BestTime,
  },
  {
    slug: "scuba-diving-in-andaman-guide",
    title: "Scuba Diving in Andaman for Beginners: Cost, Safety & Best Dive Sites",
    excerpt: "A practical, no-fluff guide to trying scuba in Andaman — including realistic pricing, safety questions, and the two most popular entry-level dive types.",
    category: "Andaman",
    date: "2025-11-15",
    author: "Deep Dive Team",
    readingTime: "8 min read",
    tags: ["andaman", "scuba", "adventure"],
    image: "https://images.pexels.com/photos/9808217/pexels-photo-9808217.png?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Scuba diver silhouette exploring a reef in Andaman",
    Content: P2_Scuba_Andaman,
  },
  {
    slug: "perfect-6-day-andaman-itinerary",
    title: "Perfect 6-Day Andaman Itinerary (Port Blair, Havelock, Neil)",
    excerpt: "The exact itinerary we recommend to first-timers — balanced across history, beach time and one slow island day. With realistic budgets.",
    category: "Andaman",
    date: "2025-11-22",
    author: "Deep Dive Team",
    readingTime: "9 min read",
    tags: ["andaman", "itinerary"],
    image: "https://images.pexels.com/photos/37949153/pexels-photo-37949153.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Serene palm-lined Andaman beach with clear water",
    Content: P3_Andaman_Itinerary,
  },
  {
    slug: "north-goa-vs-south-goa",
    title: "North Goa vs South Goa: Which Side Is Right for You?",
    excerpt: "The honest answer: they're different holidays. Here's what each side actually feels like — and why you probably shouldn't pick just one.",
    category: "Goa",
    date: "2025-12-02",
    author: "Deep Dive Team",
    readingTime: "7 min read",
    tags: ["goa", "planning"],
    image: "https://images.pexels.com/photos/28520254/pexels-photo-28520254.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Sunset at Chapora Beach with silhouetted coastline",
    Content: P4_NorthSouth_Goa,
  },
  {
    slug: "offbeat-goa-10-experiences",
    title: "Offbeat Goa: 10 Experiences Beyond the Beaches",
    excerpt: "Ten curated experiences most travelers miss — mangrove kayaking, cliff-top forts, Latin Quarter walks and a lake that bubbles methane.",
    category: "Goa",
    date: "2025-12-10",
    author: "Deep Dive Team",
    readingTime: "8 min read",
    tags: ["goa", "offbeat"],
    image: "https://images.unsplash.com/photo-1591957190485-cb718dc9812c?w=800&q=75&auto=format&fit=crop",
    imageAlt: "Palm trees reflected in still Goa waters",
    Content: P5_OffbeatGoa,
  },
  {
    slug: "goa-watersports-guide",
    title: "Complete Guide to Water Sports in Goa (Prices & Best Beaches)",
    excerpt: "Real prices, best beaches, and — most importantly — the safety questions to ask before you strap into a parasail.",
    category: "Goa",
    date: "2025-12-18",
    author: "Deep Dive Team",
    readingTime: "7 min read",
    tags: ["goa", "adventure", "watersports"],
    image: "https://images.unsplash.com/photo-1582972236019-ea4af5ffe587?w=800&q=75&auto=format&fit=crop",
    imageAlt: "Palm trees over sandy Goa beach with ocean",
    Content: P6_GoaWatersports,
  },
  {
    slug: "7-day-rajasthan-itinerary",
    title: "7-Day Rajasthan Itinerary: Forts, Lakes & the Thar Desert",
    excerpt: "Jaipur → Jodhpur → Jaisalmer in seven days, with realistic driving times and where to actually stay.",
    category: "Rajasthan",
    date: "2026-01-05",
    author: "Deep Dive Team",
    readingTime: "10 min read",
    tags: ["rajasthan", "itinerary"],
    image: "https://images.pexels.com/photos/32261804/pexels-photo-32261804.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "City Palace Jaipur intricate architecture",
    Content: P7_Rajasthan_Itinerary,
  },
  {
    slug: "jaisalmer-desert-camping-guide",
    title: "Jaisalmer Desert Camping Guide: What to Expect & How to Choose a Camp",
    excerpt: "What a desert camp actually is, how Sam and Khuri differ, and how to pick a camp that isn't a paint-and-tinsel operation.",
    category: "Rajasthan",
    date: "2026-01-12",
    author: "Deep Dive Team",
    readingTime: "8 min read",
    tags: ["rajasthan", "desert"],
    image: "https://images.unsplash.com/photo-1697464025977-cde058cfce42?w=800&q=75&auto=format&fit=crop",
    imageAlt: "Camels resting on Thar desert dunes at sunset",
    Content: P8_JaisalmerCamps,
  },
  {
    slug: "best-forts-palaces-of-rajasthan",
    title: "Best Forts and Palaces of Rajasthan, Ranked",
    excerpt: "Rajasthan has 300+ forts and palaces. Here are the ten worth planning your trip around — ranked.",
    category: "Rajasthan",
    date: "2026-01-20",
    author: "Deep Dive Team",
    readingTime: "9 min read",
    tags: ["rajasthan", "heritage"],
    image: "https://images.pexels.com/photos/34865618/pexels-photo-34865618.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Detail of Hawa Mahal pink sandstone facade",
    Content: P9_ForstsPalaces,
  },
  {
    slug: "leh-ladakh-bike-trip-planning",
    title: "Leh-Ladakh Bike Trip: Complete Planning Guide (Routes, Permits, Costs)",
    excerpt: "Manali or Srinagar? Himalayan or Classic 350? Costs, permits, gear and — importantly — realistic daily distances.",
    category: "Ladakh",
    date: "2026-02-04",
    author: "Deep Dive Team",
    readingTime: "11 min read",
    tags: ["leh-ladakh", "adventure", "biking"],
    image: "https://images.unsplash.com/photo-1663407978077-ca116e36abf0?w=800&q=75&auto=format&fit=crop",
    imageAlt: "Motorbike parked on a barren Ladakh mountain road",
    Content: P10_Ladakh_Bike,
  },
  {
    slug: "how-to-avoid-altitude-sickness-in-ladakh",
    title: "How to Avoid Altitude Sickness in Ladakh: Acclimatization Guide",
    excerpt: "AMS ruins more Ladakh trips than anything else. Here's how to spot it, how to prevent it, and our exact acclimatization plan.",
    category: "Ladakh",
    date: "2026-02-12",
    author: "Deep Dive Team",
    readingTime: "9 min read",
    tags: ["leh-ladakh", "safety", "health"],
    image: "https://images.pexels.com/photos/6576273/pexels-photo-6576273.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Himalayan mountains and valley in Ladakh",
    Content: P11_Ladakh_AMS,
  },
  {
    slug: "pangong-tso-moriri-nubra",
    title: "Pangong vs Tso Moriri vs Nubra: Building Your Ideal Ladakh Route",
    excerpt: "How to think about the three big Ladakh add-ons, and how to build a 5 or 7-day route that doesn't leave you exhausted.",
    category: "Ladakh",
    date: "2026-02-20",
    author: "Deep Dive Team",
    readingTime: "8 min read",
    tags: ["leh-ladakh", "itinerary"],
    image: "https://images.pexels.com/photos/27593915/pexels-photo-27593915.jpeg?auto=compress&cs=tinysrgb&w=800",
    imageAlt: "Blue Pangong Tso lake in Ladakh reflecting the sky",
    Content: P12_Pangong_TsoMoriri_Nubra,
  },
];

export const BLOG_CATEGORIES = ["All", "Andaman", "Goa", "Rajasthan", "Ladakh"];

export const getBlogBySlug = (slug) => BLOGS.find((b) => b.slug === slug);
